import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQuotesByJob, getQuote, sendQuote, duplicateQuote, deleteQuote } from '@/lib/api/quotes';
import type { Quote as QuoteType, QuoteDetail } from '@/lib/api/quotes';
import { quotePath } from '@/lib/utils/navPaths';
import { useWindowManager } from '@/lib/context/WindowManagerContext';
import { PrintableQuote } from '@/components/estimates/PrintableQuote';
import { printQuote, generateInvoicePdf } from '@/lib/utils/generateInvoicePdf';
import { getSettings } from '@/lib/api/settings';
import { QuoteBuilderModal } from './QuoteBuilderModal';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';

// ── Estimates & Quotes (rebuilt per CR #168) ────────────────────────────────
// One clean row per quote with dedicated columns: identity, status, total,
// created, CRUD actions, and preview (PDF / print). "Open" launches the quote
// builder as a pop-out window (minimize/maximize like everything else) instead
// of navigating away from the job.

interface EstimatesTabProps {
  jobId: string;
  clientId?: number;
  jobTitle?: string;
  siteAddress?: string;
}

interface CompanyInfo { name: string; address: string; phone: string; email: string; logo?: string }

const STATUS_META: Record<string, { label: string; icon: string; cls: string }> = {
  approved: { label: 'Approved', icon: 'check_circle', cls: 'bg-green-100 text-green-700' },
  sent:     { label: 'Sent',     icon: 'send',         cls: 'bg-blue-100 text-blue-700' },
  draft:    { label: 'Draft',    icon: 'edit',         cls: 'bg-gray-100 text-gray-600' },
  declined: { label: 'Declined', icon: 'cancel',       cls: 'bg-red-100 text-red-700' },
};

const money = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(n) || 0);
const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export const EstimatesTab = ({ jobId, clientId, jobTitle, siteAddress }: EstimatesTabProps) => {
  const navigate = useNavigate();
  const { openWindow } = useWindowManager();
  const [quotes, setQuotes] = useState<QuoteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [activeQuoteId, setActiveQuoteId] = useState<string | null>(null);
  const [sendReceipt, setSendReceipt] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<QuoteType | null>(null);
  const { toasts, removeToast, toast } = useToast();

  // Preview modal (CR #168): full printable quote with Print / Save PDF
  const [preview, setPreview] = useState<QuoteDetail | null>(null);
  const [previewBusy, setPreviewBusy] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | undefined>(undefined);
  const printableRef = useRef<HTMLDivElement>(null);

  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setQuotes(await getQuotesByJob(jobId));
    } catch {
      setError('Failed to load estimates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  // Company block for the printable header — same source the builder page uses.
  useEffect(() => {
    getSettings().then(settings => {
      const c = (settings as { company?: Record<string, string> }).company;
      if (!c) return;
      const addressParts = [
        c.address,
        c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
        c.zipCode,
      ].filter(Boolean);
      setCompanyInfo({
        name: c.companyName || 'OchrePoint Contracting',
        address: addressParts.join('\n'),
        phone: c.phone || '',
        email: c.email || '',
        logo: c.logoUrl,
      });
    }).catch(() => {});
  }, []);

  const openPreview = async (q: QuoteType) => {
    setPreviewBusy(String(q.id));
    try { setPreview(await getQuote(String(q.id))); }
    catch { toast.error('Could not load the quote for preview.'); }
    finally { setPreviewBusy(null); }
  };

  const savePreviewPdf = async () => {
    if (!printableRef.current || !preview) return;
    setPdfBusy(true);
    try { await generateInvoicePdf(printableRef.current, `${preview.quote_number || 'Quote'}.pdf`); }
    catch { toast.error('Could not build the PDF — use Print and choose "Save as PDF".'); }
    finally { setPdfBusy(false); }
  };

  // CR #168: viewing opens the builder as a pop-out window with minimize etc.
  const openAsWindow = (q: QuoteType) => {
    openWindow({
      route: quotePath(clientId, jobId, q.id),
      title: `${q.quote_number}${jobTitle ? ` — ${jobTitle}` : ''}`,
      icon: 'request_quote',
    });
  };

  const handleSend = async (q: QuoteType) => {
    try {
      setActionLoading(`send-${q.id}`);
      const result = await sendQuote(String(q.id));
      await loadQuotes();
      if (result.emailed) {
        setSendReceipt(result.emailTo ? `Quote emailed to ${result.emailTo}` : 'Quote emailed to client');
        setTimeout(() => setSendReceipt(null), 6000);
      }
    } catch { toast.error('Failed to send quote.'); }
    finally { setActionLoading(null); }
  };

  const handleDuplicate = async (q: QuoteType) => {
    try {
      setActionLoading(`dup-${q.id}`);
      const result = await duplicateQuote(String(q.id));
      await loadQuotes();
      setActiveQuoteId(String(result.id));
      setShowModal(true);
    } catch { toast.error('Failed to duplicate quote.'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const q = confirmDelete;
    setConfirmDelete(null);
    setQuotes(prev => prev.filter(x => x.id !== q.id));
    try { await deleteQuote(String(q.id)); toast.success(`${q.quote_number} deleted`); }
    catch { toast.error('Failed to delete.'); loadQuotes(); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <span className="material-symbols-outlined animate-spin text-3xl text-gray-300">progress_activity</span>
    </div>
  );

  if (error) return (
    <div className="text-center py-12">
      <p className="text-gray-900 font-semibold mb-2">Failed to load estimates</p>
      <button onClick={loadQuotes} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold">Try Again</button>
    </div>
  );

  const total = quotes.reduce((s, q) => s + (Number(q.total) || 0), 0);
  const approved = quotes.filter(q => q.status.toLowerCase() === 'approved');

  return (
    <div>
      {sendReceipt && (
        <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <span className="material-symbols-outlined text-base text-green-600">check_circle</span>
          {sendReceipt}
        </div>
      )}

      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">Estimates & Quotes</h2>
          <p className="text-gray-600 text-sm mt-1">
            {quotes.length} {quotes.length === 1 ? 'estimate' : 'estimates'} · Total {money(total)}
            {approved.length > 0 && <span className="text-green-700"> · {money(approved.reduce((s, q) => s + Number(q.total || 0), 0))} approved</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const params = new URLSearchParams({ jobId });
              if (clientId) params.set('clientId', String(clientId));
              if (jobTitle) params.set('title', jobTitle);
              if (siteAddress) params.set('address', siteAddress);
              navigate(`/quotes/wizard?${params.toString()}`);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold">
            <span className="material-symbols-outlined text-sm">auto_awesome</span>
            AI Wizard
          </button>
          <button onClick={() => { setActiveQuoteId(null); setShowModal(true); }}
            className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-semibold">
            <span className="material-symbols-outlined text-sm">add</span>
            New Estimate
          </button>
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <span className="material-symbols-outlined text-5xl text-gray-300 mb-3 block">request_quote</span>
          <p className="text-gray-600 mb-4">No estimates created yet for this job</p>
          <button onClick={() => { setActiveQuoteId(null); setShowModal(true); }}
            className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-bold">Create First Estimate</button>
        </div>
      ) : (
        /* CR #168 — clean row/column table: identity · status · total ·
           created · CRUD · preview */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-2.5 font-semibold">Estimate</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Total</th>
                  <th className="px-3 py-2.5 font-semibold">Created</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Preview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {quotes.map(q => {
                  const meta = STATUS_META[q.status.toLowerCase()] || STATUS_META.draft;
                  return (
                    <tr key={q.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button onClick={() => openAsWindow(q)}
                          title="Open in a window"
                          className="font-bold text-gray-900 hover:text-primary text-left">
                          {q.quote_number}
                        </button>
                        {(q as QuoteType & { title?: string }).title && (
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">{(q as QuoteType & { title?: string }).title}</p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
                          <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>{meta.icon}</span>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-bold text-gray-900 whitespace-nowrap">{money(q.total)}</td>
                      <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{fmtDate(q.created_at)}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => openAsWindow(q)} title="Open in a window (edit)"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 flex items-center justify-center">
                            <span className="material-symbols-outlined text-base">select_window</span>
                          </button>
                          {q.status.toLowerCase() === 'draft' && (
                            <button onClick={() => handleSend(q)} disabled={actionLoading === `send-${q.id}`}
                              title="Send to client"
                              className="h-8 w-8 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center disabled:opacity-40">
                              <span className={`material-symbols-outlined text-base ${actionLoading === `send-${q.id}` ? 'animate-spin' : ''}`}>
                                {actionLoading === `send-${q.id}` ? 'progress_activity' : 'send'}
                              </span>
                            </button>
                          )}
                          <button onClick={() => handleDuplicate(q)} disabled={actionLoading === `dup-${q.id}`}
                            title="Duplicate"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 flex items-center justify-center disabled:opacity-40">
                            <span className={`material-symbols-outlined text-base ${actionLoading === `dup-${q.id}` ? 'animate-spin' : ''}`}>
                              {actionLoading === `dup-${q.id}` ? 'progress_activity' : 'content_copy'}
                            </span>
                          </button>
                          <button onClick={() => setConfirmDelete(q)} title="Delete"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button onClick={() => openPreview(q)} disabled={previewBusy === String(q.id)}
                            title="Preview — print or save as PDF"
                            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-primary/40 text-primary text-xs font-bold hover:bg-primary/5 disabled:opacity-40">
                            <span className={`material-symbols-outlined text-base ${previewBusy === String(q.id) ? 'animate-spin' : ''}`}>
                              {previewBusy === String(q.id) ? 'progress_activity' : 'picture_as_pdf'}
                            </span>
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview modal — the real printable quote with Print / Save PDF */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-6 px-3"
          onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[900px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <span className="material-symbols-outlined text-primary">request_quote</span>
              <p className="font-bold text-gray-900 flex-1 truncate">{preview.quote_number} — preview</p>
              <button onClick={savePreviewPdf} disabled={pdfBusy}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 disabled:opacity-50">
                <span className={`material-symbols-outlined text-base ${pdfBusy ? 'animate-spin' : ''}`}>
                  {pdfBusy ? 'progress_activity' : 'download'}
                </span>
                {pdfBusy ? 'Building…' : 'Save PDF'}
              </button>
              <button onClick={() => printableRef.current && printQuote(printableRef.current)}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">
                <span className="material-symbols-outlined text-base">print</span> Print
              </button>
              <button onClick={() => setPreview(null)}
                className="h-9 w-9 rounded-lg text-gray-400 hover:bg-gray-100 flex items-center justify-center">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 overflow-x-auto">
              <PrintableQuote ref={printableRef} quote={preview} companyInfo={companyInfo} />
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete estimate"
          message={`Delete ${confirmDelete.quote_number} (${money(confirmDelete.total)})? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {showModal && (
        <QuoteBuilderModal
          jobId={jobId}
          quoteId={activeQuoteId}
          onClose={() => setShowModal(false)}
          onSaved={() => { loadQuotes(); setShowModal(false); }}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};
